import { CheckCircle, Clock, User, XCircle } from 'lucide-react'
import Button from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import {
  AppointmentStatus,
  AppointmentPaymentStatus,
  ADMIN_STATUS_NOTICES,
  NOTICE_CLASSES,
  getAppointmentStatusLabel,
  getPaymentStatusLabel,
} from '@/api/enums'
import {
  formatAppointmentTime as formatTimeSlot,
  formatDisplayDate,
} from '@/utils/date'
import { useConfirmAndCharge, useMarkAsPaid, useCompleteAppointment } from '@/api/hooks/appointments.hooks'
import { toastCallbacks, getErrorMessage } from '@/api/hooks/useMutationWithToast'
import { toast } from '@/hooks/use-toast'
import type { AppointmentResponse } from '@/api/contracts/appointments.contract'

type ActionPanelProps = {
  appointment: AppointmentResponse
  onOpenReschedule: () => void
  onOpenCancel: () => void
  onOpenNoShow: () => void
  onOpenNewDoctor: () => void
  onOpenPaymentLink: () => void
}

const Notice = ({ notice }: { notice: { tone: keyof typeof NOTICE_CLASSES; message: string } }) => {
  const cls = NOTICE_CLASSES[notice.tone]
  return <div className={cls.wrapper}><p className={cls.text}>{notice.message}</p></div>
}

export function ActionPanel({ appointment, onOpenReschedule, onOpenCancel, onOpenNoShow, onOpenNewDoctor, onOpenPaymentLink }: ActionPanelProps) {
  const confirmAndCharge = useConfirmAndCharge()
  const markPaid = useMarkAsPaid()
  const complete = useCompleteAppointment()

  const status = appointment.status
  const statusNotice = ADMIN_STATUS_NOTICES[status]
  const statusLabel = getAppointmentStatusLabel(status)
  const paymentLabel = getPaymentStatusLabel(appointment.paymentStatus)
  const hasMeaningfulPaymentLabel = paymentLabel && paymentLabel.toLowerCase() !== 'none'

  // Confirm = charge the $50 approval fee off-session against the patient's saved
  // card. The API throws 400 with an actionable message when the patient has no
  // card (fall back to a payment link) or the charge fails.
  const handleConfirm = () => {
    if (appointment.status !== AppointmentStatus.Requested) {
      toast({ title: 'Invalid action', variant: 'destructive' })
      return
    }
    confirmAndCharge.mutate(
      { appointmentId: appointment.id },
      {
        onSuccess: () =>
          toast({ title: 'Appointment confirmed — $50 approval fee charged' }),
        onError: (e) => {
          const message = getErrorMessage(e)
          if (message.toLowerCase().includes('no saved card')) {
            toast({
              title: 'Patient has no card on file',
              description:
                'Send the patient a save-card link, then confirm again.',
            })
            onOpenPaymentLink()
          } else {
            toast({ title: 'Could not confirm', description: message, variant: 'destructive' })
          }
        },
      }
    )
  }

  const summary = (
    <div className='rounded-xl border border-border/70 bg-muted/30 p-4 text-sm space-y-2'>
      <p className='text-[11px] font-semibold text-muted-foreground uppercase tracking-wide'>Scheduled for</p>
      <p className='text-sm font-semibold text-foreground'>{formatDisplayDate(appointment.startTime)} at {formatTimeSlot(appointment.startTime)}</p>
      <p className='text-xs text-muted-foreground'>with {appointment.specialistName}</p>
      {hasMeaningfulPaymentLabel && <p className='text-xs text-muted-foreground'>Payment: <span className='text-foreground'>{paymentLabel}</span></p>}
    </div>
  )

  const isActionableStatus = status === AppointmentStatus.Requested || status === AppointmentStatus.Approved

  // Both Cancel and Propose New Doctor run through the admin cancel path, which
  // refunds the fee in full with no notice window. The API refuses either once
  // the start time has passed — a visit that already happened belongs in
  // Complete or No Show — so the two buttons live and die together.
  const canCancel = appointment.cancellationPolicy.canCancel

  return (
    <Card className='shadow-sm'>
      <CardHeader className='flex flex-row items-center justify-between gap-4 p-4 pb-2 lg:p-6 lg:pb-3'>
        <div>
          <CardTitle className='text-base font-semibold'>Current Status</CardTitle>
          <p className='text-xs text-muted-foreground mt-1'>Review details and take action</p>
        </div>
      </CardHeader>
      <CardContent className='p-4 pt-0 lg:p-6 lg:pt-0'>
        {isActionableStatus ? (
          <div className='space-y-5'>
            <div className='space-y-3'>
              <h3 className='text-base font-semibold text-foreground'>{statusLabel}</h3>
              {summary}
              <Notice notice={statusNotice} />
            </div>
            <div className='grid gap-3 sm:grid-cols-2'>
              {status === AppointmentStatus.Requested && (
                <>
                  <Button onClick={handleConfirm} disabled={confirmAndCharge.isPending} className='w-full gap-2 h-11 text-sm'>
                    <CheckCircle className='h-4 w-4' />Confirm Appointment
                  </Button>
                  <Button variant='outline' onClick={onOpenPaymentLink} className='w-full gap-2 h-11 text-sm'>
                    Generate save-card link
                  </Button>
                  <Button variant='outline' onClick={onOpenReschedule} className='w-full gap-2 h-11 text-sm'>
                    <Clock className='h-4 w-4' />Propose New Time
                  </Button>
                </>
              )}
              {status === AppointmentStatus.Approved && (
                <>
                  <Button onClick={() => complete.mutate({ appointmentId: appointment.id }, toastCallbacks({ onSuccess: 'Appointment completed — remaining balance charged', onError: 'Could not complete' }))} disabled={complete.isPending} className='w-full gap-2 h-11 text-sm'>
                    <CheckCircle className='h-4 w-4' />Complete
                  </Button>
                  {appointment.paymentStatus !== AppointmentPaymentStatus.Paid && (
                    <Button variant='outline' onClick={() => markPaid.mutate({ appointmentId: appointment.id }, toastCallbacks({ onSuccess: 'Marked as paid' }))} className='w-full gap-2 h-11 text-sm'>
                      Mark as Paid (Manual)
                    </Button>
                  )}
                  <Button variant='outline' onClick={onOpenPaymentLink} className='w-full gap-2 h-11 text-sm'>Generate save-card link</Button>
                  <Button variant='outline' onClick={onOpenNoShow} className='w-full gap-2 h-11 text-sm'>
                    <Clock className='h-4 w-4' />Mark as No Show
                  </Button>
                </>
              )}
              {/* Moving the patient cancels this booking first, so it is
                  refused after the start time exactly like Cancel is. */}
              {canCancel && (
                <Button variant='outline' onClick={onOpenNewDoctor} className='w-full gap-2 h-11 text-sm'>
                  <User className='h-4 w-4' />Propose New Doctor
                </Button>
              )}
            </div>
            <div className='pt-1'>
              {canCancel ? (
                <Button variant='destructive' className='w-full gap-2 h-11 text-sm' onClick={onOpenCancel}>
                  <XCircle className='h-4 w-4' />Cancel Appointment
                </Button>
              ) : (
                <p className='text-xs text-muted-foreground'>
                  This appointment has already started, so it can no longer be cancelled or moved to another doctor. Use Complete or No Show instead.
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className='space-y-2'><Notice notice={statusNotice} /></div>
        )}
      </CardContent>
    </Card>
  )
}
